import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { configDetailsSelector } from '../../../redux/selectors/system/config-details-selector';
import { Config, ConfigDetails } from '../../../types/config/config';
import { Listing } from '../../../types/listing/listing';
import { Space } from '../../../types/listing/space';
import { generateKey } from '../../../utils/keys';
import PropertyResultView from './property-result-view';
import { configSelector } from '../../../redux/selectors/system/config-selector';
import { getMiqSizeConversions } from '../../../utils/config/unit-of-measure';
import { convertUnit } from '../../../utils/listing/unit-convertion';
import { SizeType } from '../../listingentry/sizesAndMeasurements/sizes-and-measurements';

interface RecordsDisplayProps {
    records: Listing[],
    assignMode: boolean,
    allowAssign: boolean,
    actionHandler: (record: Listing) => void,
    assignHandler?: (record: Listing) => void,
    allowUnpublish: boolean,
    spacesImport?: boolean,
    updateSpaces?: (space: any, selected: boolean) => void,
    unpublishHandler?: (record: Listing) => void

}

const RecordsDisplayView: FC<RecordsDisplayProps> = (props) => {

    const { records, assignMode, allowAssign, actionHandler, assignHandler, allowUnpublish, unpublishHandler, spacesImport, updateSpaces } = props;

    const configDetails: ConfigDetails = useSelector(configDetailsSelector);
    const config: Config = useSelector(configSelector);

    const prepareRecord = (listingRecord: Listing, selectedAvailabilities?: Space[]) => {
        const record = { ...listingRecord, specifications: { ...listingRecord.specifications }, spaces: [...listingRecord.spaces] };
        const listingType = record.listingType || '';
        if (record && record.spaces) {
            record.spaces = record.spaces.filter((space: Space) => selectedAvailabilities && selectedAvailabilities.indexOf(space) > -1);
        }
        if (record && record.spaces && record.spaces.length === 0 && record.specifications) {
            record.specifications.totalSpace = null as unknown as number;
        }
        const listingTypeForMiqSpecificationsMinPrice = config.featureFlags.listingTypeForMiqSpecificationsMinPrice;
        const extractMinMaxPriceFromSpaces = Array.isArray(listingTypeForMiqSpecificationsMinPrice) && listingTypeForMiqSpecificationsMinPrice.includes(listingType);
        if(record && Array.isArray(record.spaces) && record.specifications && extractMinMaxPriceFromSpaces) {
            if (record.spaces.length === 0) {
                record.specifications.minPrice = null as unknown as number;
                record.specifications.maxPrice = null as unknown as number;
            } else {
            const prices = record.spaces.map((sp: Space) => sp.specifications && sp.specifications.maxPrice ? sp.specifications.maxPrice : 0);
            const minn = Math.min(...prices);
            const maxx = Math.max(...prices);
            record.specifications.minPrice = isFinite(minn) ? minn : 0;
            if(record.spaces.length === 1 || minn === maxx) {
                record.specifications.minPrice = null as unknown as number; // If only one space, set minPrice to null
            }
            const nonZerominPrice = config.featureFlags.nonZeroMinPriceForMiqSpecificationsMinPrice;
            if (minn === 0 && nonZerominPrice) {
                const minPrice = prices.filter(price => price > 0);
                if (minPrice.length > 0) {
                    const nextMinPrice = Math.min(...minPrice);
                    if (nextMinPrice < maxx) {
                        record.specifications.minPrice = nextMinPrice;
                    }
                    else{
                        record.specifications.minPrice = null as unknown as number;
                    }
                }
            }
            record.specifications.maxPrice = (isFinite(maxx) && maxx > 0) ? maxx : null as unknown as number;

            if (config.featureFlags.enableMiqMinPriceNormalization) {
                if (record.spaces.length === 1 || record.specifications.minPrice === record.specifications.maxPrice) {
                    record.specifications.minPrice = record.specifications.maxPrice;
                    record.specifications.maxPrice = null as unknown as number;
                } else if (record.specifications.minPrice == null && record.specifications.maxPrice > 0) {
                    record.specifications.minPrice = maxx;
                    record.specifications.maxPrice = null as unknown as number;
                }
            }
            }
        }
        const listingTypeForMiqSpecificationsMinSpace = config.featureFlags.listingTypeForMiqSpecificationsMinSpace;
        const extractMinMaxSpaceFromSpaces = Array.isArray(listingTypeForMiqSpecificationsMinSpace) && listingTypeForMiqSpecificationsMinSpace.includes(listingType);
        if(record && Array.isArray(record.spaces) && record.specifications && extractMinMaxSpaceFromSpaces) {
            if (record.spaces.length === 0) {
                record.specifications.minSpace = null as unknown as number;
                record.specifications.maxSpace = null as unknown as number;
            } else {
            const sizes = record.spaces.map((sp: Space) => sp.specifications && sp.specifications.totalSpace ? sp.specifications.totalSpace : 0);
            const minn = Math.min(...sizes);
            const maxx = Math.max(...sizes);
            record.specifications.minSpace = isFinite(minn) ? minn : 0;
            record.specifications.maxSpace = isFinite(maxx) ? maxx : 0;
            }
        }
        if (record && Array.isArray(record.propertySizes) && record.propertySizes.length > 0) {
            const conversion = getMiqSizeConversions(config, record);
            if (conversion && Array.isArray(conversion.spaces) && conversion.spaces.length > 0) {
                record.propertySizes = (record.propertySizes || []).map((size: SizeType) => {
                    if(Array.isArray(conversion.spaces) && !conversion.spaces.includes(size.sizeKind)) {
                        return size; // Skip conversion if sizeKind is not in the conversion rules
                    }
                    size.amount = convertUnit(size.measureUnit, size.amount, conversion.unit);
                    size.measureUnit = conversion.unit;
                    return size;
                });
            }
        }
        return record;
    }

    // when the user clicks an add/edit button, prepare the data to be sent over to the create/edit listing page
    const prepareRecordForAddEdit = (listingRecord: Listing, selectedAvailabilities?: Space[]) => {
        // we need to remove any spaces that aren't in the selected availabilities so they don't get sent over to the add/edit listing page
        const record = prepareRecord(listingRecord, selectedAvailabilities);
        actionHandler(record);
    }

    const prepareRecordForAssign = (listingRecord: Listing, selectedAvailabilities?: Space[]) => {
        if (assignHandler) {
            // we need to remove any spaces that aren't in the selected availabilities so they don't get saved
            const record = prepareRecord(listingRecord, selectedAvailabilities);
            assignHandler(record);
        }
    }

    return (
        <RecordsDisplayContainer>
            {
                records.map((record: Listing, index: number) => {
                    return (
                        <React.Fragment key={String(index)}>
                            {((assignMode && record.id <= 0) || !assignMode) &&
                                <PropertyResultContainer key={generateKey()}>
                                    <PropertyResultView key={generateKey()} updateSpaces={updateSpaces}
                                        record={{ ...record, specifications: { ...record.specifications }, spaces: [...record.spaces] }}
                                        spacesImport={spacesImport ? true : false}
                                        configDetails={configDetails}
                                        displayActionBtns={!assignMode}
                                        allowAssign={allowAssign}
                                        actionHandler={prepareRecordForAddEdit}
                                        assignHandler={prepareRecordForAssign}
                                        allowUnpublish={allowUnpublish}
                                        unpublishHandler={unpublishHandler} />
                                </PropertyResultContainer>
                            }
                        </React.Fragment>
                    )
                })
            }
        </RecordsDisplayContainer>
    );
}

const RecordsDisplayContainer = styled.div``;

const PropertyResultContainer = styled.div`
    margin-bottom: 50px;
`;

export default RecordsDisplayView;